import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useToast } from '@/components/ui/use-toast';
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { downloadFromS3 } from "@/lib/file";
import { useCreateReport } from "@/lib/api/brokerage";
import { DateRange } from "../BrokerageFilesDashboard";
import { genCellKey } from "@/lib/excel";
import { useState } from "react";
import { DownloadCloudIcon, Loader, FileSpreadsheet } from "lucide-react";
import { ReportGenerationModal } from "./ReportGenerationModal";

interface FilterBarProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  presetType: "details" | "files";
  showDormanOnly?: boolean;
  onShowDormanOnlyChange?: (value: boolean) => void;
}

export function FilterBar({ presetType, dateRange, onDateRangeChange, showDormanOnly, onShowDormanOnlyChange }: FilterBarProps) {
    const [link, setLink] = useState<string | undefined>(undefined);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const { toast } = useToast();
    const today = moment.tz(CommonDestinationTimezone).endOf("day");

    const presets = presetType === "files" ? [
        {
      label: "Last 4 Days",
      getValue: () => ({
        from: today.clone().subtract(4, "days").startOf("day").format("YYYY-MM-DD"),
        to: today.format("YYYY-MM-DD"),
      }),
    },
    {
      label: "Last 14 Days",
      getValue: () => ({
        from: today.clone().subtract(14, "days").startOf("day").format("YYYY-MM-DD"),
        to: today.format("YYYY-MM-DD"),
      }),
    },
    {
      label: "Current Month",
      getValue: () => ({
        from: today.clone().startOf("month").format("YYYY-MM-DD"),
        to: today.format("YYYY-MM-DD"),
      }),
    },
    {
      label: "Last 3 Months",
      getValue: () => {
        const threeMonthsAgo = today.clone().subtract(3, "months").startOf("month");
        return {
          from: threeMonthsAgo.format("YYYY-MM-DD"),
          to: today.format("YYYY-MM-DD"),
        };
      },
    },
    {
      label: "Last 6 Months",
      getValue: () => {
        const sixMonthsAgo = today.clone().subtract(6, "months").startOf("month");
        return {
          from: sixMonthsAgo.format("YYYY-MM-DD"),
          to: today.format("YYYY-MM-DD"),
        };
      },
    },
  ] : [
    {
      label: "Today",
      getValue: () => ({
        from: today.clone().startOf("day").format("YYYY-MM-DD"),
        to: today.clone().endOf("day").format("YYYY-MM-DD"),
      }),
    },
    {
      label: "Yesterday",
      getValue: () => ({
        from: today.clone().subtract(1, "days").startOf("day").format("YYYY-MM-DD"),
        to: today.clone().subtract(1, "days").endOf("day").format("YYYY-MM-DD"),
      }),
    },
    {
      label: "Current Month",
      getValue: () => ({
        from: today.clone().startOf("month").format("YYYY-MM-DD"),
        to: today.format("YYYY-MM-DD"),
      }),
    }
  ];

  return (
        <Card className="p-4">
            <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                    {presets.map((preset) => (
                        <Button
                            key={preset.label}
                            variant="outline"
                            size="sm"
                            onClick={() => onDateRangeChange(preset.getValue())}
                        >
                            {preset.label}
                        </Button>
                    ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-4 md:grid md:grid-cols-4 items-center">
                    <div className="flex-1">
                        <Label htmlFor="date-from">From Date</Label>
                        <Input
                            id="date-from"
                            type="date"
                            value={dateRange.from}
                            onChange={(e) =>
                                onDateRangeChange({ ...dateRange, from: e.target.value })
                            }
                            max={dateRange.to}
                        />
                    </div>

                    <div className="flex-1">
                        <Label htmlFor="date-to">To Date</Label>
                        <Input
                            id="date-to"
                            type="date"
                            value={dateRange.to}
                            onChange={(e) =>
                                onDateRangeChange({ ...dateRange, to: e.target.value })
                            }
                            min={dateRange.from}
                            max={today.format("YYYY-MM-DD")}
                        />
                    </div>
                    {onShowDormanOnlyChange && (
                        <div className="flex items-center space-x-2 mt-4">
                            <Switch
                                id="show-dorman"
                                checked={showDormanOnly ?? false}
                                onCheckedChange={onShowDormanOnlyChange}
                            />
                            <Label htmlFor="show-dorman">Show Dorman rows</Label>
                        </div>
                    )}
                    <div className="flex gap-2">
                        <Button
                            id="generate_reports"
                            onClick={() => setIsReportModalOpen(true)}
                            variant="outline"
                            className="flex gap-2"
                        >
                            <FileSpreadsheet className="h-4 w-4" />
                            Generate Reports
                        </Button>
                    </div>
                </div>

            </div>
            
            <ReportGenerationModal 
                isOpen={isReportModalOpen}
                onClose={() => setIsReportModalOpen(false)}
            />
        </Card>
    );
}
